import { Client, CompatClient, StompSubscription } from '@stomp/stompjs';
import { messageCallbackType } from '@stomp/stompjs/src/types';
import { Dispatch } from 'react';
import { SOCKET_URL_STOMP } from '../../constants/rest-api-constants';
import { resubscribeToActiveSubscription } from '../../core/usecases/shared/ws/resubscribe-to-active-subscriptions';
import { markWsAsConnected, markWsAsDisconnected } from '../../store/selectors/global.selector';
import { FIVE_SECONDS, THREE_SECONDS } from '../../util/time-util';
import AnalyticsEventUtil from '../../infra/analytics/analytics-event-util';

let reconnectAttempts = 0;
const maxReconnectAttempts = 30;
const reconnectDelay = 2000;

export function getWebSocketClient(dispatch: Dispatch<any>): CompatClient {
  const client = new Client({
    brokerURL: SOCKET_URL_STOMP,
    connectionTimeout: FIVE_SECONDS,
    reconnectDelay: THREE_SECONDS,
    onChangeState: (state) => AnalyticsEventUtil.getInstance().websocketChangeState(state),
    onWebSocketError: (err) => {
      console.error('Websocket error! Err:', err);
      AnalyticsEventUtil.getInstance().websocketError(err);
      dispatch(markWsAsDisconnected());
    },
    onWebSocketClose: (evt) => {
      console.error('Websocket closed...', evt);
      AnalyticsEventUtil.getInstance().websocketClosed(evt);
      dispatch(markWsAsDisconnected());
    },
    onConnect: () => {
      AnalyticsEventUtil.getInstance().websocketConnected();
      reconnectAttempts = 0;
      dispatch(resubscribeToActiveSubscription());
      dispatch(markWsAsConnected());
    },
    onStompError: (frame) => {
      AnalyticsEventUtil.getInstance().websocketMsgError(frame.headers['message'], frame.body);
      console.error('Broker reported error: ' + frame.headers['message']);
      console.error('Additional details: ' + frame.body);
    },
    onDisconnect: (evt) => {
      console.error('Websocket disconnect...', evt);
      AnalyticsEventUtil.getInstance().websocketDisconnected();
      handleDisconnect(client as CompatClient);
      dispatch(markWsAsDisconnected());
    },
  });

  return client as CompatClient;
}

function handleDisconnect(client: CompatClient) {
  if (reconnectAttempts < maxReconnectAttempts) {
    setTimeout(() => {
      console.log('Attempting to reconnect (Attempt:', reconnectAttempts + 1, ')');
      client.activate();
      reconnectAttempts++;
    }, reconnectDelay);
  } else {
    console.error('Max reconnection attempts reached.');
  }
}

export function subscribe(client: CompatClient, destination: string, callbackFn: messageCallbackType): StompSubscription {
  return client.subscribe(destination, callbackFn);
}

export function publish(client: CompatClient, destination: string, body: any): void {
  client.publish({
    destination: destination,
    body: JSON.stringify(body),
  });
}

export function waitForSocketConnection(socket: CompatClient, callback: () => void) {
  setTimeout(function () {
    if (socket?.connected === true) {
      if (callback != null) {
        callback();
      }
    } else {
      waitForSocketConnection(socket, callback);
    }
  }, 100);
}
